/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import DynamicFieldsGrid, { renderPaymentItem } from './DynamicFieldsGrid';
import FloatingNumber from '../../../../components/input/FloatingNumber';
import { formatCurrency } from '../../../../utils/format';


export default function DiscountFields({ fields = [], control, FloatingInput, FloatingNumber: FloatingNum, cleanNumber, parentName = 'otherCostMeta', getValues, setValue, salesPriceFormattedTick }) {
  // Find discount entry presence
  const data = Array.isArray(fields) && fields.length ? fields : null;

  // onRateFormatted: recalc dollar amount for given discount index
  const onRateFormatted = (origIndex) => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return;
      //console.log('Recalculating discount amount for index:', origIndex);
      const ratePath = `${parentName}[${origIndex}].rate`;
      const rateRaw = getValues(ratePath);
      const adjustedRaw = getValues('loanInfoMeta.amount') ?? getValues('salesPrice') ?? '0';
      const sales = Number(String(adjustedRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const rate = Number(String(rateRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const amount = Number.isFinite(sales * (rate / 100)) ? sales * (rate / 100) : 0;
      const formatted = formatCurrency(amount);
      setValue(`${parentName}[${origIndex}].value`, formatted, { shouldDirty: true, shouldValidate: false });
    } catch (e) {
      // ignore
    }
  };

  // When an amount/value is formatted, recalc the rate for that index
  const onAmountFormatted = (origIndex) => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return;
      const valuePath = `${parentName}[${origIndex}].value`;
      const ratePath = `${parentName}[${origIndex}].rate`;
      const valueRaw = getValues(valuePath) || '0';
      const adjustedRaw = getValues('loanInfoMeta.amount') ?? getValues('salesPrice') ?? '0';
      const sales = Number(String(adjustedRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const amount = Number(String(valueRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const rate = sales > 0 ? (amount / sales) * 100 : 0;
      // round to 6 decimal places max then trim trailing zeros when formatted by consumer
      const rateRounded = Math.round((rate + Number.EPSILON) * 1000000) / 1000000;
      setValue(ratePath, rateRounded, { shouldDirty: true, shouldValidate: false });
    } catch (e) {
      // ignore
    }
  };

  // Recalculate discounts when the loan-adjusted base value changes.
  // Use loanInfoMeta.amount as the trigger (fallback to salesPrice if adjusted missing).
  useEffect(() => {
    if (typeof getValues !== 'function' || typeof setValue !== 'function') return undefined;
    try {
      // find discount item(s)
      for (const it of (data || [])) {
        if (!it || !it.key) continue;
        if ((it.key || '').toString().toLowerCase() !== 'discount') continue;
        const origIndex = (data || []).indexOf(it);
        onRateFormatted(origIndex);
      }
    } catch (e) {
      // ignore
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // include the adjusted value as a dependency so this effect runs when it changes
    typeof getValues === 'function' ? getValues('loanInfoMeta.amount') ?? getValues('salesPrice') : undefined,
    getValues,
    setValue,
    parentName,
  ]);

  if (!data) return null;

  return (
    <DynamicFieldsGrid
      fields={data}
      control={control}
      FloatingInput={FloatingNumber}
      cleanNumber={cleanNumber}
      renderItem={(item, ctx) => renderPaymentItem(item, {
              ...ctx,
              index: ctx.index,
              parentName: parentName,
              // ControlledField will call onFormatted(name, formatted)
              // parse the index from the name (e.g. 'discountMeta[2].rate') and trigger the appropriate handler
              onFormatted: (name, formatted) => {
                try {
                  if (!name || typeof name !== 'string') return;
                  const m = name.match(/\[(\d+)\]/);
                  const idx = m ? Number(m[1]) : ctx.index;
                  if (name.endsWith('.rate')) {
                    onRateFormatted(idx);
                  } else if (name.endsWith('.value') || name.endsWith('.amount')) {
                    onAmountFormatted(idx);
                  } else {
                    // default: recalc amount from rate
                    onRateFormatted(idx);
                  }
                } catch (e) {
                  // ignore
                }
              }
            })}
      fullRowPerItem={true}
      parentName={parentName}
      getValues={getValues}
      setValue={setValue}
      getRowClass={() => 'calc-frm-field full-dvd'}
    />
  );
}
